import React from "react";
import GenericList from "./GenericList";
import { MillenniumDateTime, SimpleDateFormat } from "@timeedit/millennium-time";
import _ from "underscore";
import { TimeEdit } from "../lib/TimeEdit";
import RC from "../lib/ReservationConstants";
import { TimeConstants as TC } from "../lib/TimeConstants";
import ReservationStatus from "../lib/ReservationStatus";
import Language from "../lib/Language";
import { Header } from "../models/Header";
const Size = Header.Label;
import TemplateKind from "../models/TemplateKind";
import { Selection } from "../models/Selection";
import TimeEditAPI from "../lib/TimeEditAPI";
import Experiment from "../models/Experiment";
import { Reservation } from "../models/Reservation";
import { Macros } from "../models/Macros";
import Log from "../lib/Log";
import { parseErrorBody } from "../lib/session/sessionErrors";
import LayerComponent, { TLayerProps } from "../lib/LayerComponent";
import EmailDialog from "./EmailDialog";
import Popover from "./Popover";
import ColumnConstants from "../lib/ColumnConstants";
import ExamConstants from "../lib/ExamConstants";
import { TAppContextProp, withAppContext } from "../contexts/AppContext";
import { COL_IDS } from "./ReservationList";
import { TAPIStatusClass } from "../types/api/APIClassTypes";
import Mousetrap from "@timeedit/mousetrap";
import { TListFind, TListSearchObject } from "../types/components/GenericList";
import { TCategoryFilterObject } from "../types/components/SearchOptions";
import { TMenuItem } from "../types/models/TMenu";
import {
    TReservationTypeListOwnProps,
    TReservationTypeListState,
} from "../types/components/ReservationTypeList";
import {
    TListCalendarInjectedProps,
    TListCalendarWrapperProps,
} from "../types/components/ListCalendarWrapper";
import { ReservationSearch } from "../models/ReservationSearch";
import { RequestSearch } from "../models/RequestSearch";
import { WaitingListSearch } from "../models/WaitingListSearch";
import { CancellationSearch } from "../models/CancellationSearch";
import { TWithId } from "../types/models/TWithId";

const { EXAM_MODE } = ExamConstants;
const { OBJECT_MASS_CHANGE: OBMC } = RC;

const { TYPE_COLUMN_OFFSET, FIELD_COLUMN_OFFSET, INFO_COLLISION, RESERVATION_SORT_ORDERS } =
    ColumnConstants;

const MAX_RESERVATIONS_MODIFY = 500;
const LIMIT_RESERVATION_DELETE = 200;

const LABEL_SIZE_CUTOFFS = {
    MINIMUM: 10,
    XS: 30,
    S: 40,
    M: 60,
    L: 110,
};

const SETTINGS_TO_SAVE = [
    "allReservations",
    "columnWidths",
    "complete",
    "includeMembers",
    "incomplete",
    "permission",
    "reservationStatus",
    "reservationVariant",
    "selectedColumns",
    "selectedInfoColumns",
    "templateGroup",
    "templateKind",
    "useTemplateGroup",
    "objectCategories",
    "reservationCategories",
    "ignoreExceptions",
    "reservationGroupsOnly",
    "placeholderType",
];

/** The calendar models the concrete lists hand this component as `data`. */
type TReservationTypeListData =
    | ReservationSearch
    | RequestSearch
    | WaitingListSearch
    | CancellationSearch;

type TReservationTypeListProps = TListCalendarWrapperProps<TReservationTypeListData> &
    TListCalendarInjectedProps &
    TReservationTypeListOwnProps &
    TLayerProps &
    TAppContextProp;

type TReservationTypeListOwnState = TReservationTypeListState & {
    selectedIds: number[];
    /** The checksum of the single selected reservation's objects, null otherwise. */
    selectedObjectCheckSum: string | null;
    hasExamField: boolean;
    hasExamValue: boolean;
    showEmailField: boolean;
    emailReservationIds: number[];
};

class ReservationTypeList extends React.Component<
    TReservationTypeListProps,
    TReservationTypeListOwnState
> {
    _isMounted?: boolean;
    /** Set by GenericList through setClearSelection / setFindFunction. */
    _clearSelection?: () => void;
    _find?: () => void;

    state: TReservationTypeListOwnState = {
        selectedIds: [],
        selectedObjectCheckSum: null,
        hasExamField: false,
        hasExamValue: false,
        showEmailField: false,
        emailReservationIds: [],
    };

    componentDidMount() {
        this._isMounted = true;

        this.props.setLayerContentProvider(this.getLayerContent);
        this.props.setLayerCloseHandler(this.onLayerClose);

        if (
            this.props.data &&
            !TemplateKind.equals(
                this.props.data.templateKind,
                this.props.searchOptions.templateKind
            )
        ) {
            this.updateTemplateKind();
        }
        if (this.props.isActive) {
            this.bindKeyboardShortcuts();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isActive && this.props.isActive) {
            this.bindKeyboardShortcuts();
        }
        if (
            !TemplateKind.equals(
                this.props.searchOptions.templateKind,
                prevProps.searchOptions.templateKind
            )
        ) {
            this.updateTemplateKind();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.unbindKeyboardShortcuts();
    }

    onSelectionChanged = (selectedReservations) => {
        const newIds = selectedReservations.map((res) => res.id);
        if (_.isEqual(newIds, this.state.selectedIds)) {
            return;
        }
        this.setState({
            selectedIds: newIds,
            selectedObjectCheckSum:
                selectedReservations.length === 1 ? selectedReservations[0].objectCheckSum : null,
        });
        if (this.props.onSelectionChanged) {
            this.props.onSelectionChanged(selectedReservations);
        }
    };

    bindKeyboardShortcuts = () => {
        Mousetrap.unbindWithHelp("mod+i");
        Mousetrap.unbindWithHelp("mod+e");
        Mousetrap.bindWithHelp(
            "mod+i",
            () => {
                this.viewInfo(this.state.selectedIds);
            },
            undefined,
            Language.get("nc_cal_show_or_hide_reservation_info.")
        );
        Mousetrap.bindWithHelp(
            "mod+e",
            () => {
                this.editFields(this.state.selectedIds);
            },
            undefined,
            Language.get("nc_cal_show_or_hide_reservation_info.")
        );
    };

    unbindKeyboardShortcuts = () => {
        Mousetrap.unbindWithHelp("mod+i");
        Mousetrap.unbindWithHelp("mod+e");
    };

    getLayerContent = () => {
        if (this.state.showEmailField === false) {
            return null;
        }
        return (
            <Popover
                target="center"
                style={{ width: Popover.DEFAULT_WIDTH }}
                onClose={this.mailDialogClosed}
            >
                <EmailDialog
                    reservations={this.state.emailReservationIds}
                    onFinished={() => {
                        this.onMailDialogDismissed();
                    }}
                    onCancel={this.onMailDialogDismissed}
                />
            </Popover>
        );
    };

    onLayerClose = () => {
        if (this.state.showEmailField) {
            this.onMailDialogDismissed();
            return;
        }
    };

    onMailDialogDismissed = () => {
        this.props.hideLayer();
        this.mailDialogClosed();
    };

    mailDialogClosed = () => {
        if (!this._isMounted) {
            return;
        }

        this.setState({
            showEmailField: false,
            emailReservationIds: [],
        });
    };

    updateTemplateKind = () => {
        if (!this.props.data) {
            return;
        }
        const newData = this.props.data.immutableSet({
            templateKind: this.props.searchOptions.templateKind,
            selection: new Selection(),
        });
        this.props.appContext.update(this.props.data, newData);
    };

    getSelection = () => {
        if (this.props.data?.privateSelected) {
            return this.props.data.selection;
        }
        return this.props.selection;
    };

    getActiveFluffy = () => {
        const selection = this.getSelection();
        if (selection.fluffy) {
            return selection.fluffy;
        }
        return null;
    };

    reportProgress = (newProgress) => {
        this.setState(newProgress);
    };

    onMassChange = (selectedLayerId, reservationIds, move, ignoreFields, callback) => {
        if (selectedLayerId === 0) {
            const modeText = move
                ? `(${Language.get("dialog_modify")})`
                : `(${Language.get("cal_res_below_copy")})`;
            const name = this.props.getLayerName?.(
                `Exp (${this.props.appContext.user?.userId}) ${modeText}`
            );
            if (!name) {
                return;
            }
            Experiment.begin(
                name,
                reservationIds,
                move,
                ignoreFields,
                this.reportProgress,
                this.props.appContext.presentModal,
                this.props.setActiveLayer,
                this.props.onLayerCreated,
                callback
            );
        } else {
            TimeEditAPI.addToReservationLayer(
                selectedLayerId,
                reservationIds,
                move,
                [],
                true,
                ignoreFields,
                null,
                (result) => {
                    const failures = (result.status ?? [])
                        .map((status, index) => ({
                            ...status,
                            reservationId: reservationIds[index],
                        }))
                        .filter((status) => status.details);
                    const numFailed = result.success ? failures.length : reservationIds.length;
                    const title = Language.get(
                        "nc_draft_x_of_y_could_not_be_added",
                        numFailed,
                        reservationIds.length
                    );
                    if (failures.length > 0) {
                        const messages = failures.map((failed, index) => (
                            <li key={`${failed.reservationId}:${index}`}>
                                <b>{failed.reservationId}:</b> {failed.details}{" "}
                            </li>
                        ));
                        this.props.appContext.presentModal(
                            <ul>{messages}</ul>,
                            null,
                            title,
                            null,
                            _.noop,
                            false,
                            true
                        );
                    } else if (!result.success) {
                        Log.error(title);
                    } else {
                        Log.info(
                            Language.tmp(
                                "{0} reservations were added to the draft",
                                reservationIds.length
                            )
                        );
                    }
                    callback(result);
                },
                (errorType, message, response) => {
                    // The dispatcher's message is the request it sent, not text for the user.
                    const reason =
                        typeof response === "string"
                            ? parseErrorBody(response).message
                            : Language.get("nc_api_connection_problem");
                    Log.error(
                        `${Language.get(
                            "nc_draft_x_of_y_could_not_be_added",
                            reservationIds.length,
                            reservationIds.length
                        )}: ${reason}`
                    );
                }
            );
        }
    };

    getReturnTypes = (columns) => {
        const selection: number[] = columns.map((column) => column.id);
        const returnTypes: number[] = [];
        selection.forEach((selectedId) => {
            let pushId: number | undefined;
            if (selectedId > TYPE_COLUMN_OFFSET && selectedId < FIELD_COLUMN_OFFSET) {
                pushId = selectedId - TYPE_COLUMN_OFFSET;
            }
            // eslint-disable-next-line no-magic-numbers
            if (selectedId === 3) {
                pushId = TimeEdit.rootType;
            }
            if (pushId !== undefined && returnTypes.indexOf(pushId) === -1) {
                returnTypes.push(pushId);
            }
        });
        return returnTypes;
    };

    getReturnFields = (columns) => {
        const selection = columns.map((column) => column.id);
        const returnFields: number[] = [];
        selection.forEach((selectedId) => {
            let pushId: number = 0;
            if (selectedId > FIELD_COLUMN_OFFSET) {
                pushId = selectedId - FIELD_COLUMN_OFFSET;
            }
            if (selectedId === COL_IDS.FIELDS) {
                pushId = TimeEdit.reservationTextField?.id ?? 0;
            }
            if (pushId !== 0 && !returnFields.includes(pushId)) {
                returnFields.push(pushId);
            }
        });
        return returnFields.map((fieldId) => ({ class: "fieldid", id: fieldId }));
    };

    getStatus = () => {
        const statuses: number[] = [...this.props.searchOptions.reservationStatus];
        if (
            TemplateKind.equals(
                TemplateKind.INFO_RESERVATION,
                this.props.searchOptions.templateKind
            )
        ) {
            statuses.push(RC.STATUS.INFO);
        } else if (
            TemplateKind.equals(TemplateKind.AVAILABILITY, this.props.searchOptions.templateKind)
        ) {
            statuses.push(RC.STATUS.AVAILABILITY);
        } else {
            statuses.push(this.props.defaultStatus);
        }

        if (this.props.searchOptions.complete) {
            statuses.push(RC.STATUS.COMPLETE);
        }
        if (this.props.searchOptions.incomplete) {
            statuses.push(RC.STATUS.INCOMPLETE);
        }
        return statuses;
    };

    getTemplateGroup = () => {
        if (!this.props.searchOptions.useTemplateGroup) {
            return 0;
        }
        const fluffy = this.getActiveFluffy();
        if (fluffy) {
            return fluffy.templateGroupId || 0;
        }
        return 0;
    };

    findReservations: TListFind<TWithId> = (
        firstIndex,
        searchObjects,
        columns,
        sortOrder,
        sortColumn,
        cb,
        longOperation = false,
        // Use default number of rows
        numberOfRows = null
    ) => {
        let reservationSortOrder = this.getSortOrder(sortOrder, sortColumn);
        let useCreated = false;
        if (
            reservationSortOrder === RESERVATION_SORT_ORDERS.CREATED_ASC ||
            reservationSortOrder === RESERVATION_SORT_ORDERS.CREATED_DESC
        ) {
            useCreated = true;
            // eslint-disable-next-line no-magic-numbers
            reservationSortOrder -= 4;
        }

        const opts = this.props.searchOptions;
        const beginTime = opts.beginTime ? new MillenniumDateTime(opts.beginTime).getMts() : 0;
        const endTime = opts.endTime
            ? new MillenniumDateTime(opts.endTime).getEndOfDay().getMts()
            : 0;
        const layerId = this.props.activeLayer;
        // The picked objects, plus a pseudo object per field that carries a
        // category filter; findReservationsList takes both in searchObjects.
        // This was `[].concat(searchObjects)`, which only type checked while
        // searchObjects was implicitly any: [] is never[].
        let so: (TListSearchObject | TCategoryFilterObject)[] = [...searchObjects];
        const objectCategories = opts.objectCategories || {};
        if (_.some(Object.keys(objectCategories), (key) => objectCategories[key].length > 0)) {
            so = so.concat(
                Object.keys(objectCategories).map((key) => ({
                    fieldId: parseInt(key, 10),
                    type: TimeEdit.rootType,
                    categories: objectCategories[key],
                }))
            );
        }

        const reservationCategories = opts.reservationCategories || {};

        const searchString = opts.placeholderType
            ? `${opts.searchString} p:"${opts.placeholderType.name}"`
            : opts.searchString;

        const query = {
            searchString,
            beginTime: (layerId ?? 0) > 0 ? 0 : beginTime,
            endTime: (layerId ?? 0) > 0 ? 0 : endTime,
            layerId,
            searchObjects: so,
            specificFields: Object.keys(reservationCategories).map((key) => ({
                id: parseInt(key, 10),
                categories: reservationCategories[key],
            })),
            reservationIds: [],
            allReservations: opts.allReservations,
            reservationStatus: this.getStatus(),
            includeMembers: opts.includeMembers,
            templateGroup: this.getTemplateGroup(),
            startRow: firstIndex,
            numberOfRows,
            sortOrder: reservationSortOrder,
            returnFields: this.getReturnFields(columns),
            returnTypes: this.getReturnTypes(columns),
            useCreatedInsteadOfModified: useCreated,
            useModifyPermission: opts.permission === RC.PERMISSION.MODIFY,
            ignoreMemberExceptions: opts.ignoreExceptions,
            reservationGroupsOnly: opts.reservationGroupsOnly,
            longOperation,
        };

        const onComplete = (result) => {
            if (!this._isMounted) {
                return;
            }

            if (result.parameters[5] === false) {
                if (
                    result.parameters[6] !== "No reservation tags were found" &&
                    result.parameters[6] !== Language.get("dynamic_reserv_list_no_match")
                ) {
                    Log.warning(result.parameters[6]);
                } else {
                    // eslint-disable-next-line no-console
                    console.log(result.parameters[6]);
                }
            }

            const numFound = result.parameters ? result.parameters[2] : result.length;
            const newData = result.parameters ? result.parameters[0] : result;

            if (this.props.activeLayer !== 0) {
                TimeEditAPI.getReservationCollisions(this.props.activeLayer, (collisions) => {
                    this.setState({ collisions });
                });
            }

            const searchIntervalBegin = result.parameters
                ? new MillenniumDateTime(result.parameters[3].datetime)
                : null;
            const searchIntervalEnd = result.parameters
                ? new MillenniumDateTime(result.parameters[4].datetime)
                : null;
            cb(newData, numFound, searchIntervalBegin, searchIntervalEnd);
        };

        // eslint-disable-next-line no-unused-vars
        const onError = () => {
            Log.warning(Language.get("nc_too_many_objects_match_search"));
        };

        if (opts.reservationVariant === RC.VARIANT.RESERVATION) {
            TimeEditAPI.findReservationsList(query, onComplete, onError);
        } else if (opts.reservationVariant === RC.VARIANT.CANCELLED) {
            TimeEditAPI.findReservationsCancelledList(query, onComplete, onError);
        }
    };

    getSortOrder = (sortOrder, sortColumn) => {
        const ASC = 0;

        // 0 RES_SORT_ID_ASC,
        // 1 RES_SORT_ID_DESC,
        if (sortColumn === Language.get("cal_reservation_list_column_id")) {
            // ID
            return sortOrder;
        }
        // 2 RES_SORT_TIME_ASC,
        // 3 RES_SORT_TIME_DESC,
        if (sortColumn === Language.get("cal_reservation_list_column_date")) {
            // Date
            return sortOrder === ASC
                ? RESERVATION_SORT_ORDERS.TIME_ASC
                : RESERVATION_SORT_ORDERS.TIME_DESC;
        }
        if (this.props.searchOptions.reservationVariant === RC.VARIANT.CANCELLED) {
            if (sortColumn !== Language.get("cal_reservation_list_column_cancelled")) {
                // Cancelled column has ID 5
                return RESERVATION_SORT_ORDERS.TIME_DESC;
            }
            return sortOrder === ASC
                ? RESERVATION_SORT_ORDERS.MODIFIED_ASC
                : RESERVATION_SORT_ORDERS.MODIFIED_DESC;
        }
        // 4 RES_SORT_MODIFIED_ASC,
        // 5 RES_SORT_MODIFIED_DESC
        if (sortColumn === Language.get("cal_reservation_list_column_modified")) {
            // Modified
            return sortOrder === ASC
                ? RESERVATION_SORT_ORDERS.MODIFIED_ASC
                : RESERVATION_SORT_ORDERS.MODIFIED_DESC;
        }
        if (sortColumn === Language.get("nc_tags_title")) {
            return sortOrder === ASC
                ? RESERVATION_SORT_ORDERS.TAGS_ASC
                : RESERVATION_SORT_ORDERS.TAGS_DESC;
        }
        // Made up values for created, transfer to 4 or 5 and set extra parameter useCreatedInsteadOfModified
        if (sortColumn === Language.get("cal_reservation_list_column_created")) {
            // Created
            return sortOrder === ASC
                ? RESERVATION_SORT_ORDERS.CREATED_ASC
                : RESERVATION_SORT_ORDERS.CREATED_DESC;
        }
        return RESERVATION_SORT_ORDERS.TIME_DESC;
    };

    isSortable = (sortColumn) => {
        return (
            sortColumn === Language.get("cal_reservation_list_column_id") ||
            sortColumn === Language.get("cal_reservation_list_column_date") ||
            sortColumn === Language.get("cal_reservation_list_column_cancelled") ||
            (sortColumn === Language.get("cal_reservation_list_column_modified") &&
                this.props.searchOptions.reservationVariant === RC.VARIANT.CANCELLED) ||
            sortColumn === Language.get("cal_reservation_list_column_modified") ||
            sortColumn === Language.get("cal_reservation_list_column_created") ||
            sortColumn === Language.get("nc_tags_title")
        );
    };

    getLabelSize = (size) => {
        if (size <= LABEL_SIZE_CUTOFFS.MINIMUM) {
            return null;
        }
        if (size < LABEL_SIZE_CUTOFFS.XS) {
            return Size.XS;
        }
        if (size < LABEL_SIZE_CUTOFFS.S) {
            return Size.S;
        }
        if (size < LABEL_SIZE_CUTOFFS.M) {
            return Size.M;
        }
        if (size < LABEL_SIZE_CUTOFFS.L) {
            return Size.L;
        }
        return Size.XL;
    };

    getTime = (time, size, isEnd) => {
        switch (this.getLabelSize(size)) {
            case Size.M:
                return SimpleDateFormat.format(
                    time,
                    isEnd
                        ? Language.getDateFormat("date_f_hh_end")
                        : Language.getDateFormat("date_f_hh")
                );
            default:
                return SimpleDateFormat.format(
                    time,
                    isEnd
                        ? Language.getDateFormat("date_f_hh_mm_end")
                        : Language.getDateFormat("date_f_hh_mm")
                );
        }
    };

    getLongDate = (date, size) => {
        switch (this.getLabelSize(size)) {
            case Size.XS:
                return SimpleDateFormat.format(date, Language.getDateFormat("date_f_d"));
            case Size.S:
                return SimpleDateFormat.format(date, Language.getDateFormat("date_f_d"));
            case Size.M:
                return SimpleDateFormat.format(date, Language.getDateFormat("date_f_m_d"));
            case Size.L:
                return SimpleDateFormat.format(date, Language.getDateFormat("date_f_ee_m_d"));
            default:
                return SimpleDateFormat.format(
                    date,
                    Language.getDateFormat("date_f_yyyy_mm_dd_hh_mm")
                );
        }
    };

    getCollisionsFor = (reservationId) => {
        if (!this.state || !this.state.collisions) {
            return false;
        }
        return _.find(this.state.collisions, (cln) => cln.id === reservationId);
    };

    emailReservations = (reservationIds) => {
        this.setState(
            {
                showEmailField: true,
                emailReservationIds: reservationIds,
            },
            this.props.showLayer
        );
    };

    viewInfo = (selectedReservationIds) => {
        this.props.onInfoOpen?.(selectedReservationIds, true);
    };

    editFields = (selectedReservationIds) => {
        this.props.onInfoOpen?.(selectedReservationIds, true, true);
    };

    getReservationMenuItems = (
        selectedReservationIds: number[],
        getDataAtIndex,
        callback,
        onMassReplace,
        refreshCallback,
        currentlyLoadedSelectedData
    ) => {
        const items: (TMenuItem | TMenuItem[])[] = [];

        if (selectedReservationIds.length === 0) {
            items.push({
                label: Language.get("nc_no_reservations_selected"),
                isDisabled: true,
                emphasize: true,
                action: _.noop,
            });
        }

        items.push([
            {
                label: Language.get("menu_view_info"),
                isDisabled:
                    selectedReservationIds.length > MAX_RESERVATIONS_MODIFY ||
                    selectedReservationIds.length === 0,
                action: () => {
                    this.viewInfo(selectedReservationIds);
                },
                classNames: ["icon", "showInfo"],
                shortcut: TimeEdit.presentShortcut("mod+i"),
            },
        ]);

        const isLocked = false;

        if (this.props.supportsBatchOperations && !isLocked) {
            items.push(
                this.getBatchMenuItems(
                    selectedReservationIds,
                    getDataAtIndex,
                    callback,
                    onMassReplace,
                    refreshCallback
                )
            );
        }

        if (this.props.getDataMenuItems && !isLocked) {
            items.push(
                this.props.getDataMenuItems(
                    selectedReservationIds,
                    getDataAtIndex,
                    callback,
                    onMassReplace
                )
            );
        }
        return items;
    };

    getLayerMenuItems = (actionFunction) => {
        const items = (this.props.layers ?? []).map((layer) => ({
            key: layer.id,
            label: layer.name,
            action: () => {
                actionFunction(layer.id);
            },
        }));
        return [
            {
                key: 0,
                label: Language.get("nc_new_draft"),
                action: () => {
                    actionFunction(0);
                },
            },
            ...items,
        ];
    };

    getBatchMenuItems = (
        selectedReservationIds,
        getDataAtIndex,
        callback,
        onMassChangeObjects,
        refreshCallback
    ) => {
        if (
            this.props.appContext.examIdField &&
            this.props.appContext.examComponentActive &&
            selectedReservationIds.length > 0
        ) {
            TimeEditAPI.exportReservations(selectedReservationIds, false, (result) => {
                let oneHasExamId = false;
                let oneHasExamField = false;
                result.forEach((res) => {
                    if (!res) {
                        return;
                    }
                    const field = _.find(
                        res.fields ?? [],
                        (f) => f.id === this.props.appContext.examIdField
                    );
                    if (field) {
                        oneHasExamField = true;
                    }
                    if (
                        field &&
                        field.values &&
                        field.values.length > 0 &&
                        field.values[0] !== ""
                    ) {
                        oneHasExamId = true;
                    }
                });

                if (
                    oneHasExamField !== this.state.hasExamField ||
                    oneHasExamId !== this.state.hasExamValue
                ) {
                    this.setState(
                        {
                            hasExamField: oneHasExamField,
                            hasExamValue: oneHasExamId,
                        },
                        () => refreshCallback?.()
                    );
                }
            });
        }
        const divider = () => ({
            isSeparator: true,
        });
        let experimentModeMenu: TMenuItem | null = null;
        if (
            this.props.activeLayer === 0 &&
            TemplateKind.equals(TemplateKind.RESERVATION, this.props.searchOptions.templateKind)
        ) {
            experimentModeMenu = {
                key: "experimentMode",
                isDisabled: selectedReservationIds.length === 0,
                label: Language.get("nc_drafts_title"),
                submenu: [
                    {
                        key: "experiment.copy",
                        label: Language.get("cal_res_below_copy"),
                        isDisabled: selectedReservationIds.length === 0,
                        submenu: this.getLayerMenuItems((selectedLayerId) => {
                            this.onMassChange(
                                selectedLayerId,
                                selectedReservationIds,
                                false,
                                false,
                                // eslint-disable-next-line no-unused-vars
                                (result) => {
                                    // Log.info("Reservations copied to draft");
                                }
                            );
                        }),
                    },
                    {
                        key: "experiment.copyNoFields",
                        label: Language.get("cal_reservation_action_button_copy_no_field"),
                        isDisabled: selectedReservationIds.length === 0,
                        submenu: this.getLayerMenuItems((selectedLayerId) => {
                            this.onMassChange(
                                selectedLayerId,
                                selectedReservationIds,
                                false,
                                true,
                                // eslint-disable-next-line no-unused-vars
                                (result) => {
                                    // Log.info("Reservations copied to draft without fields");
                                }
                            );
                        }),
                    },
                    {
                        key: "experiment.modify",
                        label: Language.get("dialog_modify"),
                        isDisabled: selectedReservationIds.length === 0,
                        submenu: this.getLayerMenuItems((selectedLayerId) => {
                            this.onMassChange(
                                selectedLayerId,
                                selectedReservationIds,
                                true,
                                false,
                                // eslint-disable-next-line no-unused-vars
                                (result) => {
                                    // Log.info("Reservations added to draft for modification");
                                }
                            );
                        }),
                    },
                ],
            };
        }
        const statusItem = this.props.allStatus
            ? {
                  key: "entry.status",
                  label: Language.get("nc_mass_change_status"),
                  isDisabled: selectedReservationIds.length === 0,
                  submenu: this.props.allStatus.map((statusId) => {
                      const status = ReservationStatus.statusForId(statusId);
                      return {
                          key: `entry.status.${status.id}`,
                          label: status.getStatusName(),
                          tooltip: _.contains(this.props.validStatus, statusId)
                              ? null
                              : Language.get("nc_mass_change_status_not_possible"),
                          isDisabled: !_.contains(this.props.validStatus, statusId),
                          action: () => this.setStatus(selectedReservationIds, status),
                      };
                  }),
              }
            : null;
        const moveItem =
            this.props.defaultStatus !== RC.STATUS.WAITING_LIST
                ? {
                      label: Language.get("nc_mass_change_move_reservations"),
                      isDisabled: selectedReservationIds.length === 0,
                      action: () => {
                          onMassChangeObjects(OBMC.MOVE);
                      },
                      classNames: ["icon", "massMoveObject"],
                  }
                : null;
        const copyAvailabilityItem =
            this.props.defaultStatus !== RC.STATUS.WAITING_LIST &&
            TemplateKind.equals(TemplateKind.AVAILABILITY, this.props.searchOptions.templateKind)
                ? {
                      label: Language.get("cal_reservation_action_button_copy"),
                      isDisabled: selectedReservationIds.length === 0,
                      action: () => {
                          onMassChangeObjects(OBMC.COPY_AVAILABILITY);
                      },
                      classNames: ["icon", "massMoveObject"],
                  }
                : null;
        const cancelReservations = (sendEmail) => {
            TimeEditAPI.okToCancelReservations(
                { reservationIds: selectedReservationIds },
                (res) => {
                    const canCancelAll = _.every(res.parameters[0], (status) => !status.details);
                    if (!canCancelAll) {
                        this.props.appContext.presentModal(
                            <div>
                                {
                                    <p>
                                        {Language.get(
                                            "cal_res_below_reservations_can_not_be_cancelled"
                                        )}
                                    </p>
                                }
                            </div>,
                            null,
                            Language.get("cal_res_below_cancel_reservations")
                        );
                        return;
                    }

                    const yesButton = {
                        title: Language.get("dialog_yes"),
                        cb: () => {
                            const calls = _.splitArray(
                                selectedReservationIds,
                                LIMIT_RESERVATION_DELETE
                            ).map((batch) => (done) => {
                                Reservation.cancel(batch, () => {
                                    done();
                                });
                            });
                            _.runSync(calls, () => {
                                if (sendEmail) {
                                    this.emailReservations(selectedReservationIds);
                                }
                                this.onUpdate([]);
                                if (this._clearSelection) {
                                    this._clearSelection();
                                }
                                if (this._find) {
                                    this._find();
                                }
                            });
                        },
                    };
                    const noButton = { title: Language.get("dialog_no") };
                    const buttons = [yesButton, noButton];
                    this.props.appContext.presentModal(
                        <div>
                            {
                                <p>
                                    {Language.get(
                                        "cal_res_below_do_you_wish_to_cancel_all",
                                        selectedReservationIds.length
                                    )}
                                </p>
                            }
                        </div>,
                        null,
                        Language.get("cal_res_below_cancel_reservations"),
                        buttons
                    );
                }
            );
        };

        const cancelSubmenu = [
            {
                label: Language.get("cal_func_res_delete"),
                isDisabled: selectedReservationIds.length === 0,
                action: () => {
                    cancelReservations(false);
                },
            },
            {
                label: Language.get("cal_func_res_delete_email"),
                isDisabled:
                    selectedReservationIds.length === 0 ||
                    !TimeEdit.isEmailActive ||
                    this.props.activeLayer !== 0,
                action: () => cancelReservations(true),
            },
        ];

        const autoObjectItem = {
            label: Language.get("nc_menu_item_automatic_object_selection"),
            isDisabled: selectedReservationIds.length === 0,
            action: () => {
                onMassChangeObjects(OBMC.ASSIGN);
            },
            classNames: ["icon", "massAssignObject"],
        };

        let requestLabel = Language.get("nc_create_exam_request");
        if (selectedReservationIds.length > 1) {
            requestLabel = Language.get("nc_create_exam_requests");
        } else if (this.state.hasExamValue) {
            requestLabel = Language.get("nc_edit_exam_request");
        }
        const examRequestItem =
            this.props.appContext.examComponentActive && this.state.hasExamField
                ? {
                      key: "reservationList.createExamRequest",
                      label: requestLabel,
                      isDisabled: selectedReservationIds.length < 1,
                      action: () => {
                          let mode = this.state.hasExamValue ? EXAM_MODE.EDIT : EXAM_MODE.CREATE;
                          if (selectedReservationIds.length > 1) {
                              mode = EXAM_MODE.CREATE_MULTI;
                          }
                          TimeEditAPI.getReservationsByExtid(selectedReservationIds, (result) => {
                              this.props.appContext.createExamRequest(result, mode);
                          });
                      },
                  }
                : null;

        const result: (TMenuItem | null)[] = [
            divider(),
            {
                label: Language.get("nc_mass_change_add_object"),
                isDisabled: selectedReservationIds.length === 0,
                action: () => {
                    onMassChangeObjects(OBMC.ADD);
                },
                classNames: ["icon", "massAddObject"],
            },
            {
                label: Language.get("nc_mass_change_remove_object"),
                isDisabled: selectedReservationIds.length === 0,
                action: () => {
                    onMassChangeObjects(OBMC.REMOVE);
                },
                classNames: ["icon", "massRemoveObject"],
            },
            {
                label: Language.get("nc_mass_change_replace_object"),
                isDisabled: selectedReservationIds.length === 0,
                action: () => {
                    onMassChangeObjects(OBMC.REPLACE);
                },
                classNames: ["icon", "massReplaceObject"],
            },
            moveItem,
            copyAvailabilityItem,
            autoObjectItem,
            divider(),
            {
                label: Language.get("nc_cal_func_res_edit_fields"),
                isDisabled:
                    selectedReservationIds.length > MAX_RESERVATIONS_MODIFY ||
                    selectedReservationIds.length === 0,
                action: () => {
                    this.editFields(selectedReservationIds);
                },
                classNames: ["icon", "editFields"],
                shortcut: TimeEdit.presentShortcut("mod+e"),
            },
            {
                key: "table_select_all",
                label: Language.get("cal_reservation_action_button_select_all"),
                action: () => {
                    callback(true);
                },
                classNames: ["icon", "selectAll"],
                shortcut: TimeEdit.presentShortcut("mod+a"),
            },
            {
                key: "entry.cancelMenu",
                label: Language.get("cal_func_res_delete"),
                isDisabled: selectedReservationIds.length === 0,
                submenu: cancelSubmenu,
            },
        ];
        if (statusItem) {
            result.push(divider());
            result.push(statusItem);
        }
        if (experimentModeMenu) {
            result.push(divider());
            result.push(experimentModeMenu);
        }
        if (examRequestItem) {
            result.push(divider());
            result.push(examRequestItem);
        }

        result.push(divider());
        result.push({
            key: "list.createGroup",
            label: Language.get("nc_create_reservation_group"),
            isDisabled: selectedReservationIds.length === 0, // TODO: Perhaps disable if the selection already contains groups, since each reservation may only belong to one group
            action: () => {
                const name = this.props.appContext.user?.promptForGroupName
                    ? // eslint-disable-next-line no-alert
                      prompt(Language.get("nc_reservation_group_name"))
                    : "";
                if (name === null) {
                    return;
                }
                TimeEditAPI.createReservationGroupWithReservations(
                    name,
                    _.flatten(selectedReservationIds),
                    // eslint-disable-next-line no-shadow
                    () => {
                        this.onUpdate(selectedReservationIds);
                    },
                    (errorMessage) => Log.error(errorMessage)
                );
            },
        });

        return result.filter((item) => item !== null);
    };

    _setClearSelection = (clearFunction) => {
        this._clearSelection = clearFunction;
    };

    _setFindFunction = (findFunction) => {
        this._find = findFunction;
    };

    setStatus = (selectedReservationIds: number[], status) => {
        const setStatus = () => {
            let numSuccessful = 0;
            let errors: TAPIStatusClass[] = [];
            const calls = _.splitArray(selectedReservationIds, TimeEditAPI.STATUS_BATCH_SIZE).map(
                (batch) => (done) => {
                    TimeEditAPI.setReservationStatus(batch, status.id, (result) => {
                        numSuccessful += result[0].filter((bool) => bool === true).length;
                        errors = [...errors, ...result[1].filter((status) => status.details)];
                        // Update progress?
                        done();
                    });
                }
            );
            _.runSync(calls, () => {
                let errorDetails = "";
                if (errors.length > 0) {
                    errorDetails = errors.map((error) => error.details).join(", ");
                }
                if (numSuccessful === 0) {
                    Log.warning(
                        `${Language.get("nc_edit_reservation_status_change_error")} ${errorDetails}`
                    );
                    return;
                }
                if (numSuccessful < selectedReservationIds.length) {
                    Log.warning(
                        `${Language.get(
                            "nc_edit_reservation_status_change_x_of_y_complete",
                            numSuccessful,
                            selectedReservationIds.length
                        )} ${errorDetails}`
                    );
                } else {
                    Log.info(Language.get("nc_edit_reservation_status_change_complete"));
                }
                this.onUpdate(selectedReservationIds);
            });
        };

        TimeEditAPI.getPreferences(
            "dismissedModalDialogs.confirm_mass_status_change",
            undefined,
            undefined,
            (value) => {
                if (!value || value === null || process.env.NODE_ENV === "development") {
                    const buttons = [
                        { title: Language.get("dialog_cancel") },
                        { title: Language.get("nc_dialog_proceed"), cb: setStatus },
                    ];
                    this.props.appContext.presentModal(
                        <p>
                            {Language.get(
                                "nc_confirm_mass_status",
                                selectedReservationIds.length,
                                status.getStatusName()
                            )}
                        </p>,
                        "confirm_mass_status_change",
                        null,
                        buttons
                    );
                } else {
                    setStatus();
                }
            }
        );
    };

    getSearchHelp = (onClose?) => {
        if (this.props.getSearchHelp) {
            return this.props.getSearchHelp();
        }
        return (
            <div className="reservationSearchHelp">
                {onClose ? <button className="deprecated clearSearch" onClick={onClose} /> : null}
                <table>
                    <tbody>
                        <tr>
                            <td>#</td>
                            <td>{Language.get("nc_search_help_reservation_id")}</td>
                        </tr>
                        <tr>
                            <td>##</td>
                            <td>{Language.get("nc_search_help_object_id")}</td>
                        </tr>
                        <tr>
                            <td>$</td>
                            <td>{Language.get("nc_autopilot_id")}</td>
                        </tr>
                        <tr>
                            <td>@</td>
                            <td>{Language.get("nc_search_help_changed_or_created_by")}</td>
                        </tr>
                        <tr>
                            <td>@@</td>
                            <td>{Language.get("nc_search_help_created_by")}</td>
                        </tr>
                        <tr>
                            <td>m:</td>
                            <td>{Language.get("nc_search_help_change_date")}</td>
                        </tr>
                        <tr>
                            <td>t:</td>
                            <td>{Language.get("nc_search_help_has_objects_of_type")}</td>
                        </tr>
                        <tr>
                            <td>f:</td>
                            <td>{Language.get("nc_search_help_has_reservation_text")}</td>
                        </tr>
                        <tr>
                            <td>p:</td>
                            <td>{Language.get("nc_search_help_has_placeholder_for_type")}</td>
                        </tr>
                        <tr>
                            <td>g:</td>
                            <td>{Language.get("nc_search_help_group_name_or_id")}</td>
                        </tr>
                        <tr>
                            <td>tag:</td>
                            <td>{Language.get("nc_search_help_tag_name")}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    onUpdate = (selectedReservationIds) => {
        this.props.appContext.fireEvent(
            `reservationList${this.props.id}`,
            Macros.Event.RESERVATION_MADE_OR_MODIFIED,
            selectedReservationIds,
            false,
            false
        );
        this.props.refresh?.();
    };

    getCellLabels = (reservation, columns, columnWidths) => {
        let color;
        let textColor;
        let bold;

        const labels = columns.map((column, i) => {
            if (!reservation) {
                return "";
            }
            if (column.name === Language.get("cal_reservation_list_column_id")) {
                return reservation.id;
            }
            const width = columnWidths[i];
            if (column.name === Language.get("cal_reservation_list_column_type_all")) {
                if (!reservation.objects) {
                    return "";
                }
                const rootId = TimeEdit.rootType !== undefined ? TimeEdit.rootType : 0;
                return reservation.objects
                    .filter((object) => object.type.id === rootId)
                    .map((object) => {
                        if (object.fields && object.fields[0].values) {
                            return object.fields[0].values[0];
                        }
                        return "-";
                    })
                    .join(", ");
            }
            if (column.id > TYPE_COLUMN_OFFSET && column.id < FIELD_COLUMN_OFFSET) {
                if (!reservation.objects) {
                    return "";
                }
                const typeId = column.id - TYPE_COLUMN_OFFSET;
                return reservation.objects
                    .filter((object) => object.type.id === typeId)
                    .map((object) => {
                        if (object.fields && object.fields[0].values) {
                            return object.fields[0].values[0];
                        }
                        return "-";
                    })
                    .join(", ");
            }
            if (column.id > FIELD_COLUMN_OFFSET) {
                if (!reservation.fields) {
                    return "";
                }
                const fieldId = column.id - FIELD_COLUMN_OFFSET;
                return reservation.fields
                    .filter((field) => field.id === fieldId)
                    .map((field) => (field.values ? field.values.join(", ") : ""));
            }
            if (column.name === Language.get("cal_reservation_list_column_field_all")) {
                if (!reservation.fields) {
                    return "";
                }
                return reservation.fields
                    .map((field) => (field.values ? field.values.join(", ") : null))
                    .filter((val) => val !== "")
                    .join(", ");
            }
            let format;
            const startTime = reservation.begin ? new MillenniumDateTime(reservation.begin) : null;
            const endTime = reservation.end ? new MillenniumDateTime(reservation.end) : null;
            const between = startTime && endTime ? startTime.daysBetween(endTime) : null;
            const isSameDay = between === 0 || (between === 1 && endTime?.isMidnight());
            if (column.name === Language.get("cal_reservation_list_column_date") && startTime) {
                if (!isSameDay) {
                    // Different start and end date
                    format = Language.getDateFormat("date_f_m_d");
                    const start = SimpleDateFormat.format(startTime, format);
                    const end = SimpleDateFormat.format(
                        endTime!,
                        Language.getDateFormat("date_f_m_d_end")
                    );
                    if (start !== end) {
                        return `${start}-${end}`;
                    }
                    return start;
                }
                format = Language.getDateFormat("date_f_yy_mm_dd");
                return SimpleDateFormat.format(startTime, format);
            }
            if (
                column.name === Language.get("cal_reservation_list_column_time") &&
                reservation.begin
            ) {
                return `${this.getTime(startTime, width, false)}-${this.getTime(
                    endTime,
                    width,
                    true
                )}`;
            }
            if (column.name === Language.get("cal_res_side_user_time_zone")) {
                return reservation.timezone || "";
            }
            if (column.name === Language.get("cal_reservation_list_column_length")) {
                const length = reservation.length
                    ? reservation.length
                    : reservation.end - reservation.begin;
                if (length === 0 || isNaN(length)) {
                    return "0:00";
                }
                const hours = Math.floor(length / TC.SECONDS_PER_HOUR);
                const minutes = Math.floor((length % TC.SECONDS_PER_HOUR) / TC.SECONDS_PER_MINUTE);
                // eslint-disable-next-line no-magic-numbers
                return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
            }
            if (column.name === Language.get("cal_reservation_list_column_status")) {
                if (!reservation.status) {
                    return ReservationStatus.C_CONFIRMED.getStatusName(false);
                }

                let status = reservation.status;
                if (
                    _.some(
                        status,
                        (item) =>
                            item.status === RC.STATUS.REQUESTED ||
                            item.status === RC.STATUS.REJECTED
                    )
                ) {
                    status = status.filter((item) => item.status !== RC.STATUS.INCOMPLETE);
                }
                return status
                    .map((item) => ReservationStatus.statusForId(item.status).getStatusName(false))
                    .join(", ");
            }
            if (column.name === Language.get("cal_reservation_list_column_created")) {
                let created = "";
                if (reservation.created) {
                    const createdTime = new MillenniumDateTime(reservation.created || 0);
                    created = this.getLongDate(createdTime, width);
                }
                const createdBy = reservation.createdby ? `, ${reservation.createdby}` : "";
                return created + createdBy;
            }
            if (column.name === Language.get("cal_reservation_list_column_modified")) {
                let modified = "";
                if (reservation.modified) {
                    const modifiedTime = new MillenniumDateTime(reservation.modified || 0);
                    modified = this.getLongDate(modifiedTime, width);
                }
                const modifiedBy = reservation.modifiedby ? `, ${reservation.modifiedby}` : "";
                return modified + modifiedBy;
            }
            if (column.name === Language.get("cal_reservation_list_column_cancelled")) {
                if (reservation.cancelled) {
                    const cancelledTime = new MillenniumDateTime(reservation.cancelled || 0);
                    return this.getLongDate(cancelledTime, width);
                }
                return "";
            }
            if (column.name === Language.get("cal_reservation_list_column_week")) {
                if (!startTime || !endTime) {
                    return "";
                }
                return Language.formatWeekTableCell(
                    startTime,
                    endTime,
                    this.props.appContext.customWeekNames
                );
            }
            if (column.name === Language.get("cal_reservation_list_column_week_day")) {
                if (!startTime || !endTime) {
                    return "";
                }
                const start = SimpleDateFormat.format(startTime, "EE");
                const end = SimpleDateFormat.format(endTime, "LL");
                if (start === end) {
                    return start;
                }
                return `${start} - ${end}`;
            }

            if (column.name === Language.get("nc_tags_title")) {
                return reservation.tags ? reservation.tags.join(", ") : "";
            }

            if (column.name === Language.get("nc_mass_change_collisions")) {
                const col = this.getCollisionsFor(reservation.id);
                if (!col || col.collidingReservations.length === 0) {
                    if (col && col.collidingInfoReservations.length > 0) {
                        return INFO_COLLISION;
                    }
                    return "";
                }
                return `${col.collidingReservations.length}`;
            }

            if (column.name === Language.get("nc_reservation_exception_title")) {
                if (!reservation.memberExceptionCount) {
                    return "";
                }
                return `${reservation.memberExceptionCount}`;
            }

            if (column.name === Language.get("nc_reservation_group_list_title")) {
                if (!reservation.group) {
                    return "";
                }
                return reservation.group.name
                    ? reservation.group.name
                    : reservation.group.extid
                    ? reservation.group.extid
                    : reservation.group.id;
            }

            if (column.name === Language.get("nc_capacity")) {
                if (!reservation.capacity && reservation.capacity !== 0) {
                    return "";
                }
                return `${reservation.capacity}`;
            }
            if (column.name === Language.get("nc_remaining_capacity")) {
                if (this.props.appContext.user?.useSecondarySize) {
                    if (
                        !reservation.remainingCapacitySecond &&
                        reservation.remainingCapacitySecond !== 0
                    ) {
                        return "";
                    }
                    return `${reservation.remainingCapacitySecond}`;
                }
                if (!reservation.remainingCapacity && reservation.remainingCapacity !== 0) {
                    return "";
                }
                return `${reservation.remainingCapacity}`;
            }
            if (column.name === Language.get("nc_size")) {
                if (this.props.appContext.user?.useSecondarySize) {
                    if (!reservation.sizeSecond && reservation.sizeSecond !== 0) {
                        return "";
                    }
                    return `${reservation.sizeSecond}`;
                }
                if (!reservation.size && reservation.size !== 0) {
                    return "";
                }
                return `${reservation.size}`;
            }
            return "";
        });

        return { labels, color, textColor, bold };
    };

    render() {
        return (
            <GenericList
                {...this.props}
                progress={this.state ? this.state.progress : null}
                getDataMenuItems={this.getReservationMenuItems}
                collisions={this.state ? this.state.collisions : []}
                onSelectionChanged={this.onSelectionChanged}
                settingsToSave={SETTINGS_TO_SAVE}
                getCellLabels={this.getCellLabels}
                isSortable={this.isSortable}
                find={this.findReservations}
                setClearSelection={this._setClearSelection}
                setFindFunction={this._setFindFunction}
                getSearchHelp={this.getSearchHelp}
            />
        );
    }
}

export default LayerComponent.wrap(withAppContext(ReservationTypeList));
